import React, { useState, useMemo, useEffect } from 'react'
import { RoleToggle } from './RoleToggle.jsx'
import { SessionList } from './SessionList.jsx'
import { ReportSelector } from './ReportSelector.jsx'
import { InvitationBanner } from './InvitationBanner.jsx'
import { TasksNavEntry } from './nav/TasksNavEntry.jsx'
import { Button, IconButton, Icon, Pill, InfoCircleIcon, GearIcon, SignOutIcon, MegaphoneIcon } from '../ui'
import { useContext } from 'react'
import { AuthContext } from '../auth/AuthProvider.jsx'
import { FlagsContext } from '../flags/FlagsProvider.jsx'

function NotifyFormWrapper(props) {
  const [Form, setForm] = useState(null)
  useEffect(() => {
    import('./notifications/SendNotificationForm.jsx').then((m) => setForm(() => m.SendNotificationForm))
  }, [])
  if (!Form) return <span style={{ fontSize: 12, color: 'var(--text-secondary)', padding: '8px 0', display: 'block' }}>Loading...</span>
  return <div style={{ padding: '4px 0 8px' }}><Form {...props} /></div>
}

const BRAND_GREEN = '#3B6D11'

function NavTabButton({ icon, label, active = false, onClick }) {
  const color = active ? BRAND_GREEN : 'var(--text-secondary)'
  return (
    <button
      type="button"
      className="nav-tab"
      onClick={onClick}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      style={{ color, fontWeight: active ? 500 : 400 }}
    >
      <span style={tabStyles.iconWrap}>{icon}</span>
      <span style={tabStyles.label}>{label}</span>
    </button>
  )
}

const tabStyles = {
  iconWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 16,
    height: 16,
  },
  label: {
    fontSize: 10,
    lineHeight: 1.2,
  },
}

const PROGRESS_LABEL_ORDER = [
  { value: 'on_track', label: 'On track' },
  { value: 'ahead_of_plan', label: 'Ahead of plan' },
  { value: 'at_risk', label: 'At risk' },
  { value: 'needs_focus', label: 'Needs focus' },
]

// Goal lifecycle → Pill tone. `dropped` reads as archived (not critical),
// so it stays neutral; `danger` is reserved for unresolved errors.
const STATUS_TONE = {
  draft:    'neutral',
  active:   'success',
  achieved: 'success',
  dropped:  'neutral',
}

function GoalNavItem({ goal, selectedGoalId, onSelectGoal, onDeleteGoal }) {
  const [confirmDelete, setConfirmDelete] = useState(false)
  const isSelected = selectedGoalId === goal.goalId
  const isDraft = goal.status === 'draft'

  function handleDeleteClick(e) {
    e.stopPropagation()
    setConfirmDelete(true)
  }
  function handleConfirmDelete(e) {
    e.stopPropagation()
    onDeleteGoal?.(goal.goalId)
    setConfirmDelete(false)
  }
  function handleCancelDelete(e) {
    e.stopPropagation()
    setConfirmDelete(false)
  }

  return (
    <div
      style={{ ...goalNavStyles.item, ...(isSelected ? goalNavStyles.itemActive : {}) }}
      onClick={() => onSelectGoal?.(goal.goalId)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onSelectGoal?.(goal.goalId)}
    >
      <span style={goalNavStyles.title}>{goal.title}</span>
      <div style={goalNavStyles.trailing}>
        <Pill tone={STATUS_TONE[goal.status] || 'neutral'}>{goal.status}</Pill>
        {isDraft && (
          confirmDelete ? (
            <span style={goalNavStyles.deleteConfirmGroup} onClick={(e) => e.stopPropagation()}>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleConfirmDelete}
                style={{ color: 'var(--danger-text)', fontWeight: 600 }}
              >
                Delete
              </Button>
              <Button size="sm" variant="ghost" onClick={handleCancelDelete}>No</Button>
            </span>
          ) : (
            <IconButton ariaLabel="Delete goal" onClick={handleDeleteClick}>×</IconButton>
          )
        )}
      </div>
    </div>
  )
}

const GOAL_SECTIONS = [
  { value: 'not_started', label: 'Not started' },
  ...PROGRESS_LABEL_ORDER,
]

function goalSection(goal) {
  if (goal.status === 'draft') return 'not_started'
  return goal.statusLabel || 'not_started'
}

function GoalNavList({ goals, filter, selectedGoalId, onSelectGoal, onDeleteGoal }) {
  const filtered = filter === 'all' ? goals : goals.filter((g) => g.status === filter)
  const commonProps = { selectedGoalId, onSelectGoal, onDeleteGoal }

  return (
    <div>
      {GOAL_SECTIONS.map(({ value, label }) => {
        const group = filtered.filter((g) => goalSection(g) === value)
        if (group.length === 0) return null
        return (
          <div key={value}>
            <p style={goalNavStyles.subLabel}>{label}</p>
            {group.map((g) => (
              <GoalNavItem key={g.goalId} goal={g} {...commonProps} />
            ))}
          </div>
        )
      })}
    </div>
  )
}

function reflectionDisplayState(r) {
  if (r.closedAt) return { label: 'Closed', tone: 'neutral' }
  if (r.sharedAt) return { label: 'Shared', tone: 'success' }
  if (r.readyAt) return { label: 'Ready', tone: 'success' }
  return { label: 'Draft', tone: 'neutral' }
}

function ReflectionNavItem({ reflection, selectedReflectionId, onSelectReflection, onDeleteReflection }) {
  const [confirmDelete, setConfirmDelete] = useState(false)
  const isSelected = selectedReflectionId === reflection.reflectionId
  const isOpen = reflection.status === 'open'
  const displayState = reflectionDisplayState(reflection)

  function handleDeleteClick(e) {
    e.stopPropagation()
    setConfirmDelete(true)
  }
  function handleConfirmDelete(e) {
    e.stopPropagation()
    onDeleteReflection?.(reflection.reflectionId)
    setConfirmDelete(false)
  }
  function handleCancelDelete(e) {
    e.stopPropagation()
    setConfirmDelete(false)
  }

  return (
    <div
      style={{ ...goalNavStyles.item, ...(isSelected ? goalNavStyles.itemActive : {}), ...(reflection.status === 'closed' ? { opacity: 0.6 } : {}) }}
      onClick={() => onSelectReflection?.(reflection.reflectionId)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onSelectReflection?.(reflection.reflectionId)}
    >
      <span style={goalNavStyles.title}>{reflection.title}</span>
      <div style={goalNavStyles.trailing}>
        <Pill tone={displayState.tone}>{displayState.label}</Pill>
        {isOpen && !reflection.readyAt && onDeleteReflection && (
          confirmDelete ? (
            <span style={goalNavStyles.deleteConfirmGroup} onClick={(e) => e.stopPropagation()}>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleConfirmDelete}
                style={{ color: 'var(--danger-text)', fontWeight: 600 }}
              >
                Delete
              </Button>
              <Button size="sm" variant="ghost" onClick={handleCancelDelete}>No</Button>
            </span>
          ) : (
            <IconButton ariaLabel="Delete reflection" onClick={handleDeleteClick}>×</IconButton>
          )
        )}
      </div>
    </div>
  )
}

function ReflectionNavList({ reflections, filter, selectedReflectionId, onSelectReflection, onDeleteReflection }) {
  const filtered = filter === 'all' ? reflections : reflections.filter((r) => r.status === filter)
  const sorted = [...filtered].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))

  return (
    <div>
      {sorted.map((r) => (
        <ReflectionNavItem
          key={r.reflectionId}
          reflection={r}
          selectedReflectionId={selectedReflectionId}
          onSelectReflection={onSelectReflection}
          onDeleteReflection={onDeleteReflection}
        />
      ))}
    </div>
  )
}

const goalNavStyles = {
  item: {
    padding: '7px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
  },
  itemActive: { background: 'var(--border)' },
  title: { fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)', lineHeight: 1.3, flex: 1 },
  trailing: { display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 },
  subLabel: {
    fontSize: '10px', fontWeight: 600, color: 'var(--text-secondary)',
    textTransform: 'uppercase', letterSpacing: '0.05em',
    padding: '6px 10px 2px', margin: 0,
  },
  deleteConfirmGroup: { display: 'flex', gap: '4px', flexShrink: 0, alignItems: 'center' },
}

export function Sidebar({
  isManager,
  role,
  onRoleChange,
  sessions,
  selectedSessionId,
  onSelectSession,
  onNewSession,
  onDeleteSession,
  reports,
  selectedReportId,
  onSelectReport,
  onAddReport,
  onRemoveReport,
  reportSessions,
  onSignOut,
  settingsOpen,
  onOpenSettings,
  goals = [],
  selectedGoalId,
  onSelectGoal,
  onNewGoal,
  onDeleteGoal,
  invitations,
  onApproveInvitation,
  onDeclineInvitation,
  deletionPending = false,
  docsOpen = false,
  onOpenDocs,
  tasksOpen = false,
  onOpenTasks,
  onNewTask,
  onSelectReportSession,
  onOpenFeedback,
  reflections = [],
  selectedReflectionId,
  onSelectReflection,
  onNewReflection,
  onDeleteReflection,
  me = null,
}) {
  // Read auth context directly (not via useAuth) so Sidebar stays renderable
  // in isolation (Storybook, tests) without requiring an AuthProvider wrapper.
  const authCtx = useContext(AuthContext)
  const orgSlug = authCtx?.user?.org || null
  const flags = useContext(FlagsContext)
  const [collapsed, setCollapsed] = useState(false)
  const [activeTag, setActiveTag] = useState(null)
  const [notifyFormOpen, setNotifyFormOpen] = useState(false)
  const [goalsCollapsed, setGoalsCollapsed] = useState(false)
  const [reflectionsCollapsed, setReflectionsCollapsed] = useState(false)
  const [sessionsCollapsed, setSessionsCollapsed] = useState(false)
  const [reportsCollapsed, setReportsCollapsed] = useState(false)
  const [goalsFilter, setGoalsFilter] = useState(role === 'team' ? 'active' : 'all')
  const [reflectionsFilter, setReflectionsFilter] = useState('all')
  const [sessionsFilter, setSessionsFilter] = useState(role === 'team' ? 'ready' : 'all')

  // Seed the team-view defaults when entering team. Leaving team preserves
  // the user's explicit filter choices across admin/prep round-trips —
  // only the tag filter is team-scoped and always clears on exit.
  useEffect(() => {
    if (role === 'team') {
      setGoalsFilter('active')
      setSessionsFilter('ready')
    } else {
      setActiveTag(null)
    }
  }, [role])

  const allTags = useMemo(() => {
    const tags = new Set()
    reports.forEach((r) => (r.tags || []).forEach((t) => tags.add(t)))
    return [...tags].sort()
  }, [reports])

  const filteredReports = useMemo(() => {
    if (!activeTag) return reports
    return reports.filter((r) => (r.tags || []).includes(activeTag))
  }, [reports, activeTag])

  if (collapsed) {
    return (
      <nav className="sidebar-scope" style={styles.collapsed} aria-label="Primary navigation">
        <IconButton ariaLabel="Expand sidebar" onClick={() => setCollapsed(false)}>
          <Icon name="chevron-right" />
        </IconButton>
      </nav>
    )
  }

  return (
    <nav className="sidebar-scope" style={styles.sidebar} aria-label="Primary navigation">
      {/* Brand header */}
      <div style={styles.brand}>
        <div style={styles.brandLeft}>
          <img src="/logo-mark.svg" alt="" width={26} height={26} />
          <div style={styles.brandText}>
            <span style={styles.brandNameRow}>
              <span style={styles.brandName}>Soundcheck</span>
              {orgSlug && (
                <span style={styles.orgPill} title="Your organization">{orgSlug}</span>
              )}
            </span>
            <span style={styles.brandTagline}>Prepare. Align. Grow.</span>
          </div>
        </div>
        <IconButton ariaLabel="Collapse sidebar" title="Collapse sidebar" onClick={() => setCollapsed(true)}>
          <Icon name="chevron-left" />
        </IconButton>
      </div>

      {/* Pending invitations */}
      <InvitationBanner
        invitations={invitations}
        onApprove={onApproveInvitation}
        onDecline={onDeclineInvitation}
      />

      {/* Role toggle (managers only) */}
      {isManager && (
        <div style={styles.section}>
          <RoleToggle role={role} onRoleChange={onRoleChange} isManager={isManager} />
        </div>
      )}

      <div style={styles.scrollable}>
        {onNewTask && (
          <div style={styles.section}>
            <button
              style={styles.newBtn}
              onClick={onNewTask}
              aria-label="New task"
            >+ New task</button>
          </div>
        )}
        {role === 'prep' ? (
          <>
            {/* ── Goals section ── */}
            <div style={styles.sectionHeader}>
              <button style={styles.sectionHeaderBtn} onClick={() => setGoalsCollapsed((c) => !c)}>
                <span style={styles.sectionHeaderLabel}>Goals</span>
                <span style={styles.chevron}>{goalsCollapsed ? '›' : '⌄'}</span>
              </button>
            </div>
            {!goalsCollapsed && (
              <div style={styles.sectionBody}>
                <div style={styles.filterRow}>
                  {[
                    { value: 'all', label: 'All' },
                    { value: 'draft', label: 'Draft' },
                    { value: 'active', label: 'Active' },
                    { value: 'achieved', label: 'Achieved' },
                    { value: 'dropped', label: 'Dropped' },
                  ].map(({ value, label }) => (
                    <button
                      key={value}
                      style={{ ...styles.filterPill, ...(goalsFilter === value ? styles.filterPillActive : {}) }}
                      onClick={() => setGoalsFilter(value)}
                    >{label}</button>
                  ))}
                </div>
                <button style={styles.newBtn} onClick={onNewGoal}>+ New goal</button>
                <GoalNavList
                  goals={goals}
                  filter={goalsFilter}
                  selectedGoalId={selectedGoalId}
                  onSelectGoal={onSelectGoal}
                  onDeleteGoal={onDeleteGoal}
                />
              </div>
            )}

            {/* ── Reflections section (feature-flagged) ── */}
            {flags?.reflections?.enabled && (
              <>
                <div style={styles.sectionHeader}>
                  <button style={styles.sectionHeaderBtn} onClick={() => setReflectionsCollapsed((c) => !c)}>
                    <span style={styles.sectionHeaderLabel}>Reflections</span>
                    <span style={styles.chevron}>{reflectionsCollapsed ? '›' : '⌄'}</span>
                  </button>
                </div>
                {!reflectionsCollapsed && (
                  <div style={styles.sectionBody}>
                    <div style={styles.filterRow}>
                      {[
                        { value: 'all', label: 'All' },
                        { value: 'open', label: 'Open' },
                        { value: 'closed', label: 'Closed' },
                      ].map(({ value, label }) => (
                        <button
                          key={value}
                          style={{ ...styles.filterPill, ...(reflectionsFilter === value ? styles.filterPillActive : {}) }}
                          onClick={() => setReflectionsFilter(value)}
                        >{label}</button>
                      ))}
                    </div>
                    <button style={styles.newBtn} onClick={onNewReflection}>+ New reflection</button>
                    <ReflectionNavList
                      reflections={reflections}
                      filter={reflectionsFilter}
                      selectedReflectionId={selectedReflectionId}
                      onSelectReflection={onSelectReflection}
                      onDeleteReflection={onDeleteReflection}
                    />
                  </div>
                )}
              </>
            )}

            {/* ── Sessions section ── */}
            <div style={styles.sectionHeader}>
              <button style={styles.sectionHeaderBtn} onClick={() => setSessionsCollapsed((c) => !c)}>
                <span style={styles.sectionHeaderLabel}>Sessions</span>
                <span style={styles.chevron}>{sessionsCollapsed ? '›' : '⌄'}</span>
              </button>
            </div>
            {!sessionsCollapsed && (
              <div style={styles.sectionBody}>
                <div style={styles.filterRow}>
                  {[
                    { value: 'all', label: 'All' },
                    { value: 'draft', label: 'Draft' },
                    { value: 'ready', label: 'Ready' },
                    { value: 'completed', label: 'Completed' },
                    { value: 'cancelled', label: 'Cancelled' },
                  ].map(({ value, label }) => (
                    <button
                      key={value}
                      style={{ ...styles.filterPill, ...(sessionsFilter === value ? styles.filterPillActive : {}) }}
                      onClick={() => setSessionsFilter(value)}
                    >{label}</button>
                  ))}
                </div>
                <button
                  style={{ ...styles.newBtn, ...(deletionPending ? styles.newBtnDisabled : {}) }}
                  onClick={deletionPending ? undefined : onNewSession}
                  disabled={deletionPending}
                  title={deletionPending ? 'Account deletion is scheduled' : undefined}
                >+ New session</button>
                <div style={{ marginTop: '6px' }}>
                  <SessionList
                    sessions={
                      sessionsFilter === 'all'
                        ? sessions
                        : sessions.filter((s) => s.status === sessionsFilter)
                    }
                    selectedSessionId={selectedSessionId}
                    onSelectSession={onSelectSession}
                    onDeleteSession={onDeleteSession}
                  />
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div style={styles.sectionHeader}>
              <button style={styles.sectionHeaderBtn} onClick={() => setReportsCollapsed((c) => !c)}>
                <span style={styles.sectionHeaderLabel}>Direct reports</span>
                <span style={styles.chevron}>{reportsCollapsed ? '›' : '⌄'}</span>
              </button>
            </div>
            {!reportsCollapsed && <div style={styles.sectionBody}>
              {allTags.length > 0 && (
                <div style={styles.tagFilter}>
                  <button
                    style={{ ...styles.tagPill, ...(activeTag === null ? styles.tagPillActive : {}) }}
                    onClick={() => setActiveTag(null)}
                  >All</button>
                  {allTags.map((tag) => (
                    <button
                      key={tag}
                      style={{ ...styles.tagPill, ...(activeTag === tag ? styles.tagPillActive : {}) }}
                      onClick={() => setActiveTag(activeTag === tag ? null : tag)}
                    >{tag}</button>
                  ))}
                </div>
              )}
              <ReportSelector
                reports={filteredReports}
                selectedReportId={selectedReportId}
                onSelectReport={onSelectReport}
                onAddReport={onAddReport}
                onRemoveReport={onRemoveReport}
                showAdd={false}
              />
              <button
                style={styles.newBtn}
                onClick={() => setNotifyFormOpen((o) => !o)}
              >
                {notifyFormOpen ? 'Cancel' : 'Send notification'}
              </button>
              {notifyFormOpen && me && (
                <NotifyFormWrapper
                  me={me}
                  reports={reports}
                  onSent={() => setNotifyFormOpen(false)}
                  onCancel={() => setNotifyFormOpen(false)}
                />
              )}
            </div>}
            {selectedReportId && (
              <>
                {/* ── Goals section ── */}
                <div style={styles.sectionHeader}>
                  <button style={styles.sectionHeaderBtn} onClick={() => setGoalsCollapsed((c) => !c)}>
                    <span style={styles.sectionHeaderLabel}>Goals</span>
                    <span style={styles.chevron}>{goalsCollapsed ? '›' : '⌄'}</span>
                  </button>
                </div>
                {!goalsCollapsed && (
                  <div style={styles.sectionBody}>
                    <div style={styles.filterRow}>
                      {[
                        { value: 'all', label: 'All' },
                        { value: 'draft', label: 'Draft' },
                        { value: 'active', label: 'Active' },
                        { value: 'achieved', label: 'Achieved' },
                        { value: 'dropped', label: 'Dropped' },
                      ].map(({ value, label }) => (
                        <button
                          key={value}
                          style={{ ...styles.filterPill, ...(goalsFilter === value ? styles.filterPillActive : {}) }}
                          onClick={() => setGoalsFilter(value)}
                        >{label}</button>
                      ))}
                    </div>
                    <button style={styles.newBtn} onClick={onNewGoal}>+ New goal</button>
                    <GoalNavList
                      goals={goals}
                      filter={goalsFilter}
                      selectedGoalId={selectedGoalId}
                      onSelectGoal={onSelectGoal}
                      onDeleteGoal={onDeleteGoal}
                    />
                  </div>
                )}

                {/* ── Reflections section (feature-flagged) ── */}
                {flags?.reflections?.enabled && (
                  <>
                    <div style={styles.sectionHeader}>
                      <button style={styles.sectionHeaderBtn} onClick={() => setReflectionsCollapsed((c) => !c)}>
                        <span style={styles.sectionHeaderLabel}>Reflections</span>
                        <span style={styles.chevron}>{reflectionsCollapsed ? '›' : '⌄'}</span>
                      </button>
                    </div>
                    {!reflectionsCollapsed && (
                      <div style={styles.sectionBody}>
                        <div style={styles.filterRow}>
                          {[
                            { value: 'all', label: 'All' },
                            { value: 'open', label: 'Open' },
                            { value: 'closed', label: 'Closed' },
                          ].map(({ value, label }) => (
                            <button
                              key={value}
                              style={{ ...styles.filterPill, ...(reflectionsFilter === value ? styles.filterPillActive : {}) }}
                              onClick={() => setReflectionsFilter(value)}
                            >{label}</button>
                          ))}
                        </div>
                        <button style={styles.newBtn} onClick={onNewReflection}>+ New reflection</button>
                        <ReflectionNavList
                          reflections={reflections}
                          filter={reflectionsFilter}
                          selectedReflectionId={selectedReflectionId}
                          onSelectReflection={onSelectReflection}
                          onDeleteReflection={onDeleteReflection}
                        />
                      </div>
                    )}
                  </>
                )}

                {/* ── Sessions section ── */}
                <div style={styles.sectionHeader}>
                  <button style={styles.sectionHeaderBtn} onClick={() => setSessionsCollapsed((c) => !c)}>
                    <span style={styles.sectionHeaderLabel}>Sessions</span>
                    <span style={styles.chevron}>{sessionsCollapsed ? '›' : '⌄'}</span>
                  </button>
                </div>
                {!sessionsCollapsed && reportSessions && (
                  <div style={styles.sectionBody}>
                    <div style={styles.filterRow}>
                      {[
                        { value: 'all', label: 'All' },
                        { value: 'draft', label: 'Draft' },
                        { value: 'ready', label: 'Ready' },
                        { value: 'completed', label: 'Completed' },
                        { value: 'cancelled', label: 'Cancelled' },
                      ].map(({ value, label }) => (
                        <button
                          key={value}
                          style={{ ...styles.filterPill, ...(sessionsFilter === value ? styles.filterPillActive : {}) }}
                          onClick={() => setSessionsFilter(value)}
                        >{label}</button>
                      ))}
                    </div>
                    <SessionList
                      sessions={
                        sessionsFilter === 'all'
                          ? reportSessions
                          : reportSessions.filter((s) => s.status === sessionsFilter)
                      }
                      selectedSessionId={selectedSessionId}
                      onSelectSession={onSelectReportSession || onSelectSession}
                      readOnly
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      <div style={styles.footer}>
        {onOpenTasks ? (
          <TasksNavEntry
            tasksOpen={tasksOpen}
            onOpenTasks={onOpenTasks}
            me={me}
          />
        ) : (
          <div className="nav-tab" aria-hidden="true" />
        )}
        <NavTabButton
          icon={<InfoCircleIcon />}
          label="About"
          active={docsOpen}
          onClick={onOpenDocs}
        />
        <NavTabButton
          icon={<MegaphoneIcon />}
          label="Feedback"
          onClick={onOpenFeedback}
        />
        <NavTabButton
          icon={<GearIcon />}
          label="Settings"
          active={settingsOpen}
          onClick={onOpenSettings}
        />
        <NavTabButton
          icon={<SignOutIcon />}
          label="Sign out"
          onClick={onSignOut}
        />
      </div>
    </nav>
  )
}

const styles = {
  sidebar: {
    width: '280px',
    minWidth: '280px',
    background: 'var(--sidebar-bg)',
    borderRight: '1px solid var(--border)',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  collapsed: {
    width: '32px',
    minWidth: '32px',
    borderRight: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'flex-start',
    padding: '8px 4px',
    background: 'var(--sidebar-bg)',
  },
  collapseBtn: {
    background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px',
    color: 'var(--text-secondary)', padding: '4px',
  },
  brand: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '21px 21px 16px',
    borderBottom: '1px solid var(--border)',
  },
  brandLeft: { display: 'flex', alignItems: 'center', gap: '10px' },
  brandText: { display: 'flex', flexDirection: 'column', lineHeight: 1.1 },
  brandName: { fontSize: '18.2px', fontWeight: 700, letterSpacing: '-0.02em' },
  brandNameRow: { display: 'inline-flex', alignItems: 'center', gap: '6px' },
  orgPill: {
    fontSize: '10px',
    fontWeight: 600,
    padding: '2px 7px',
    borderRadius: 'var(--radius-pill)',
    background: 'var(--accent-brand-bg)',
    color: 'var(--accent-brand-text)',
    lineHeight: 1.4,
  },
  brandTagline: { fontSize: '13px', color: 'var(--text-secondary)', marginTop: 3 },
  iconBtn: {
    background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px',
    color: 'var(--text-secondary)', padding: '4px 6px', borderRadius: '4px',
  },
  scrollable: { flex: 1, overflowY: 'auto', padding: '8px 0' },
  section: { padding: '8px 12px' },
  sectionLabel: { fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' },
  tagFilter: { display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' },
  tagPill: {
    fontSize: '10px', padding: '3px 8px', borderRadius: '10px', border: '1px solid var(--accent-brand)',
    background: 'transparent', color: 'var(--accent-brand)', cursor: 'pointer',
  },
  tagPillActive: { background: 'var(--accent-brand)', color: '#fff', borderColor: 'var(--accent-brand)' },
  navLink: {
    width: '100%',
    padding: '7px 10px',
    background: 'transparent',
    color: 'var(--text-secondary)',
    border: 'none',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 400,
    cursor: 'pointer',
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  navLinkActive: {
    background: 'var(--border)',
    color: 'var(--text-primary)',
    fontWeight: 600,
  },
  navLinkIcon: {
    opacity: 0.5,
    fontSize: '10px',
  },
  newBtn: {
    width: '100%',
    padding: '10px 12px',
    background: 'transparent',
    color: 'var(--text-primary)',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    textAlign: 'left',
  },
  newBtnDisabled: {
    color: 'var(--text-secondary)',
    cursor: 'not-allowed',
  },
  sectionHeader: {
    padding: '0 12px',
  },
  sectionHeaderBtn: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'var(--accent-brand)',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    padding: '5px 8px',
    marginBottom: '2px',
  },
  sectionHeaderLabel: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#fff',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  chevron: {
    fontSize: '12px',
    color: '#fff',
    opacity: 0.7,
  },
  sectionBody: {
    padding: '2px 12px 8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  filterRow: {
    display: 'flex',
    gap: '4px',
    marginBottom: '4px',
  },
  filterPill: {
    fontSize: '10px',
    padding: '2px 8px',
    borderRadius: '10px',
    border: '1px solid var(--accent-brand)',
    background: 'transparent',
    color: 'var(--accent-brand)',
    cursor: 'pointer',
  },
  filterPillActive: {
    background: 'var(--accent-brand)',
    color: '#fff',
    borderColor: 'var(--accent-brand)',
  },
  footer: {
    padding: '6px 0 8px',
    borderTop: '0.5px solid var(--border)',
    display: 'flex',
    alignItems: 'stretch',
    width: '100%',
  },
}
